import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  Chip,
  Alert,
  Tabs,
  Tab,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
// Removed date picker imports due to compatibility issues
import { addDays } from 'date-fns';
import couponService from '../../services/couponService';
import { Coupon, CouponCreateRequest } from '../../types/coupon';

interface CreateCouponDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editingCoupon?: Coupon | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
  </div>
);

const CreateCouponDialog: React.FC<CreateCouponDialogProps> = ({
  open,
  onClose,
  onSuccess,
  editingCoupon
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newTerm, setNewTerm] = useState('');

  const [formData, setFormData] = useState<CouponCreateRequest>({
    title: '',
    description: '',
    type: 'flat',
    value: 0,
    minOrderValue: 0,
    maxDiscount: undefined,
    usageLimit: 100,
    userUsageLimit: 1,
    validFrom: new Date().toISOString(),
    validUntil: addDays(new Date(), 30).toISOString(),
    isFeatured: false,
    imageUrl: '',
    applicableServices: [],
    applicableUserTypes: [],
    applicableLocations: [],
    termsAndConditions: [],
    generateType: 'custom',
    customCode: '',
    quantity: 1,
    codePrefix: 'PICKUP'
  });

  // Form validation
  const validatePositiveNumber = (value: string): boolean => {
    const num = parseFloat(value);
    return !isNaN(num) && num >= 0;
  };

  const validatePercentage = (value: string): boolean => {
    const num = parseFloat(value);
    return !isNaN(num) && num >= 0 && num <= 100;
  };

  const handlePositiveNumberChange = (field: keyof CouponCreateRequest, value: string) => {
    if (value === '' || validatePositiveNumber(value)) {
      setFormData(prev => ({ 
        ...prev, 
        [field]: value === '' ? 0 : parseFloat(value) 
      }));
    }
  };

  const handleDiscountValueChange = (value: string) => {
    if (formData.type === 'percentage') {
      if (value === '' || validatePercentage(value)) {
        setFormData(prev => ({ 
          ...prev, 
          value: value === '' ? 0 : parseFloat(value) 
        }));
      }
    } else {
      if (value === '' || validatePositiveNumber(value)) {
        setFormData(prev => ({ 
          ...prev, 
          value: value === '' ? 0 : parseFloat(value) 
        }));
      }
    }
  };

  const handleMaxDiscountChange = (value: string) => {
    if (value === '') {
      setFormData(prev => ({ ...prev, maxDiscount: undefined }));
    } else if (validatePositiveNumber(value)) {
      setFormData(prev => ({ ...prev, maxDiscount: parseFloat(value) }));
    }
  };

  useEffect(() => {
    if (editingCoupon) {
      setFormData({
        title: editingCoupon.title,
        description: editingCoupon.description,
        type: editingCoupon.type,
        value: editingCoupon.value,
        minOrderValue: editingCoupon.minOrderValue,
        maxDiscount: editingCoupon.maxDiscount,
        usageLimit: editingCoupon.usageLimit,
        userUsageLimit: editingCoupon.userUsageLimit,
        validFrom: editingCoupon.validFrom,
        validUntil: editingCoupon.validUntil,
        isFeatured: editingCoupon.isFeatured,
        imageUrl: editingCoupon.imageUrl || '',
        applicableServices: editingCoupon.applicableServices || [],
        applicableUserTypes: editingCoupon.applicableUserTypes || [],
        applicableLocations: editingCoupon.applicableLocations || [],
        termsAndConditions: editingCoupon.termsAndConditions,
        generateType: 'custom',
        customCode: editingCoupon.code,
        quantity: 1,
        codePrefix: 'PICKUP'
      });
    }
  }, [editingCoupon]);

  const handleSubmit = async () => {
    setError(null);
    
    // Validation
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }
    if (!formData.description.trim()) {
      setError('Description is required');
      return;
    }
    if (!formData.value || formData.value <= 0) {
      setError('Discount value must be greater than 0');
      return;
    }
    if (formData.type === 'percentage' && formData.value > 100) {
      setError('Percentage discount cannot be more than 100%');
      return;
    }
    if (formData.minOrderValue < 0) {
      setError('Minimum order value cannot be negative');
      return;
    }
    if (!formData.usageLimit || formData.usageLimit <= 0) {
      setError('Total usage limit must be greater than 0');
      return;
    }
    if (!formData.userUsageLimit || formData.userUsageLimit <= 0) {
      setError('Per user limit must be greater than 0');
      return;
    }
    if (formData.maxDiscount !== undefined && formData.maxDiscount <= 0) {
      setError('Maximum discount must be greater than 0');
      return;
    }
    if (!editingCoupon && formData.generateType === 'custom' && !formData.customCode?.trim()) {
      setError('Custom code is required');
      return;
    }
    if (!editingCoupon && formData.generateType === 'random' && (!formData.quantity || formData.quantity <= 0)) {
      setError('Quantity must be greater than 0');
      return;
    }

    setLoading(true);

    try {
      // Prepare the data by removing undefined values
      const cleanFormData = { ...formData };
      
      // Remove undefined maxDiscount to prevent Firestore errors
      if (cleanFormData.maxDiscount === undefined) {
        delete (cleanFormData as any).maxDiscount;
      }

      if (editingCoupon) {
        // Update existing coupon
        const updates: any = {
          title: cleanFormData.title,
          description: cleanFormData.description,
          type: cleanFormData.type,
          value: cleanFormData.value,
          minOrderValue: cleanFormData.minOrderValue,
          usageLimit: cleanFormData.usageLimit,
          userUsageLimit: cleanFormData.userUsageLimit,
          validFrom: cleanFormData.validFrom,
          validUntil: cleanFormData.validUntil,
          isFeatured: cleanFormData.isFeatured,
          imageUrl: cleanFormData.imageUrl,
          termsAndConditions: cleanFormData.termsAndConditions,
        };

        // Only include maxDiscount if it has a value
        if (cleanFormData.maxDiscount !== undefined) {
          updates.maxDiscount = cleanFormData.maxDiscount;
        }

        await couponService.updateCoupon(editingCoupon.id, updates);
      } else {
        // Create new coupon
        await couponService.createCoupons(cleanFormData, 'admin-user-id'); // Replace with actual admin ID
      }
      onSuccess();
    } catch (error: any) {
      setError(error.message || 'Failed to save coupon');
    } finally {
      setLoading(false);
    }
  };

  const addTerm = () => {
    if (newTerm.trim()) {
      setFormData(prev => ({
        ...prev,
        termsAndConditions: [...prev.termsAndConditions, newTerm.trim()]
      }));
      setNewTerm('');
    }
  };

  const removeTerm = (index: number) => {
    setFormData(prev => ({
      ...prev,
      termsAndConditions: prev.termsAndConditions.filter((_, i) => i !== index)
    }));
  };

  const handleClose = () => {
    if (!editingCoupon) {
      // Reset form for new coupon creation
      setFormData({
        title: '',
        description: '',
        type: 'flat',
        value: 0,
        minOrderValue: 0,
        maxDiscount: undefined,
        usageLimit: 100,
        userUsageLimit: 1,
        validFrom: new Date().toISOString(),
        validUntil: addDays(new Date(), 30).toISOString(),
        isFeatured: false,
        imageUrl: '',
        applicableServices: [],
        applicableUserTypes: [],
        applicableLocations: [],
        termsAndConditions: [],
        generateType: 'custom',
        customCode: '',
        quantity: 1,
        codePrefix: 'PICKUP'
      });
    }
    setError(null);
    setActiveTab(0);
    setNewTerm('');
    onClose();
  };

  return (
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCoupon ? 'Edit Coupon' : 'Create New Coupon'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab label="Basic Info" />
            <Tab label="Conditions" />
            <Tab label="Advanced" />
          </Tabs>

          {/* Basic Information Tab */}
          <TabPanel value={activeTab} index={0}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="Title"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                required
              />
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                required
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Discount Type</InputLabel>
                  <Select
                    value={formData.type}
                    label="Discount Type"
                    onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as 'flat' | 'percentage' }))}
                  >
                    <MenuItem value="flat">Flat Amount (₹)</MenuItem>
                    <MenuItem value="percentage">Percentage (%)</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label={formData.type === 'flat' ? 'Discount Amount (₹)' : 'Discount Percentage (%)'}
                  value={formData.value || ''}
                  onChange={(e) => handleDiscountValueChange(e.target.value)}
                  error={formData.type === 'percentage' 
                    ? !validatePercentage(formData.value?.toString() || '0') 
                    : !validatePositiveNumber(formData.value?.toString() || '0')}
                  helperText={
                    formData.type === 'percentage' 
                      ? (!validatePercentage(formData.value?.toString() || '0') ? 'Please enter a percentage between 0-100' : '')
                      : (!validatePositiveNumber(formData.value?.toString() || '0') ? 'Please enter a valid positive number' : '')
                  }
                  required
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                {formData.type === 'percentage' && (
                  <TextField
                    fullWidth
                    label="Maximum Discount (₹)"
                    value={formData.maxDiscount || ''}
                    onChange={(e) => handleMaxDiscountChange(e.target.value)}
                    error={formData.maxDiscount !== undefined && !validatePositiveNumber(formData.maxDiscount?.toString())}
                    helperText={formData.maxDiscount !== undefined && !validatePositiveNumber(formData.maxDiscount?.toString()) ? 'Please enter a valid positive number' : 'Leave blank for no limit'}
                  />
                )}
                <TextField
                  fullWidth
                  label="Minimum Order Value (₹)"
                  value={formData.minOrderValue || ''}
                  onChange={(e) => handlePositiveNumberChange('minOrderValue', e.target.value)}
                  error={!validatePositiveNumber(formData.minOrderValue?.toString() || '0')}
                  helperText={!validatePositiveNumber(formData.minOrderValue?.toString() || '0') ? 'Please enter a valid positive number' : ''}
                  required
                />
              </Box>
            </Box>
          </TabPanel>

          {/* Conditions Tab */}
          <TabPanel value={activeTab} index={1}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Total Usage Limit"
                  value={formData.usageLimit || ''}
                  onChange={(e) => handlePositiveNumberChange('usageLimit', e.target.value)}
                  error={!validatePositiveNumber(formData.usageLimit?.toString() || '0')}
                  helperText={!validatePositiveNumber(formData.usageLimit?.toString() || '0') ? 'Please enter a valid positive number' : 'Maximum number of times this coupon can be used'}
                  required
                />
                <TextField
                  fullWidth
                  label="Per User Limit"
                  value={formData.userUsageLimit || ''}
                  onChange={(e) => handlePositiveNumberChange('userUsageLimit', e.target.value)}
                  error={!validatePositiveNumber(formData.userUsageLimit?.toString() || '0')}
                  helperText={!validatePositiveNumber(formData.userUsageLimit?.toString() || '0') ? 'Please enter a valid positive number' : 'How many times each user can use this coupon'}
                  required
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Valid From"
                  type="datetime-local"
                  value={new Date(formData.validFrom).toISOString().slice(0, 16)}
                  onChange={(e) => setFormData(prev => ({ ...prev, validFrom: new Date(e.target.value).toISOString() }))}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  label="Valid Until"
                  type="datetime-local"
                  value={new Date(formData.validUntil).toISOString().slice(0, 16)}
                  onChange={(e) => setFormData(prev => ({ ...prev, validUntil: new Date(e.target.value).toISOString() }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Terms and Conditions
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Enter a term or condition..."
                    value={newTerm}
                    onChange={(e) => setNewTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addTerm()}
                    sx={{ flexGrow: 1 }}
                  />
                  <Button size="small" onClick={addTerm} startIcon={<AddIcon />}>
                    Add
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {formData.termsAndConditions.map((term, index) => (
                    <Chip
                      key={index}
                      label={term}
                      onDelete={() => removeTerm(index)}
                      deleteIcon={<DeleteIcon />}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          </TabPanel>

          {/* Advanced Tab */}
          <TabPanel value={activeTab} index={2}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {!editingCoupon && (
                <>
                  <FormControl fullWidth>
                    <InputLabel>Code Generation</InputLabel>
                    <Select
                      value={formData.generateType}
                      label="Code Generation"
                      onChange={(e) => setFormData(prev => ({ ...prev, generateType: e.target.value as 'custom' | 'random' }))}
                    >
                      <MenuItem value="custom">Custom Code</MenuItem>
                      <MenuItem value="random">Random Codes</MenuItem>
                    </Select>
                  </FormControl>
                  {formData.generateType === 'custom' ? (
                    <TextField
                      fullWidth
                      label="Custom Code"
                      value={formData.customCode}
                      onChange={(e) => setFormData(prev => ({ ...prev, customCode: e.target.value.toUpperCase() }))}
                      required
                    />
                  ) : (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField
                        fullWidth
                        label="Quantity"
                        value={formData.quantity || ''}
                        onChange={(e) => handlePositiveNumberChange('quantity', e.target.value)}
                        error={!validatePositiveNumber(formData.quantity?.toString() || '0') || (formData.quantity || 0) < 1}
                        helperText={!validatePositiveNumber(formData.quantity?.toString() || '0') || (formData.quantity || 0) < 1 ? 'Please enter a number greater than 0' : ''}
                        required
                      />
                      <TextField
                        fullWidth
                        label="Code Prefix"
                        value={formData.codePrefix}
                        onChange={(e) => setFormData(prev => ({ ...prev, codePrefix: e.target.value.toUpperCase() }))}
                      />
                    </Box>
                  )}
                </>
              )}
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isFeatured}
                    onChange={(e) => setFormData(prev => ({ ...prev, isFeatured: e.target.checked }))}
                  />
                }
                label="Featured Coupon (Show in app highlights)"
              />
              <TextField
                fullWidth
                label="Image URL (Optional)"
                value={formData.imageUrl}
                onChange={(e) => setFormData(prev => ({ ...prev, imageUrl: e.target.value }))}
              />
            </Box>
          </TabPanel>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {editingCoupon ? 'Update' : 'Create'} Coupon
          </Button>
        </DialogActions>
      </Dialog>
  );
};

export default CreateCouponDialog;